<template>
  <v-dialog
    v-model="dialog.estatus"
    max-width="960"
    persistent
    scrollable
  >
    <v-card>
      <v-card-title class="primary white--text py-3">
        <v-icon left dark>mdi-content-copy</v-icon>
        Copiar maestros entre ciclos
        <v-spacer></v-spacer>
        <v-btn icon dark @click="cerrar"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>

      <v-card-text class="pt-4">
        <v-alert dense outlined type="info" class="mb-4">
          Copia los <strong>maestros permanentes</strong> de <code>maestrosgrupo</code> del ciclo origen
          hacia los grupos consecutivos del ciclo destino (ej. grupo 1000 → 1001).
        </v-alert>

        <v-row dense>
          <v-col cols="12" md="6">
            <v-autocomplete
              v-model="cicloOrigen"
              :items="ciclos"
              label="Ciclo origen (raíz)"
              filled
              dense
              hide-details
              return-object
              item-text="ciclo"
              item-value="id_ciclo"
              prepend-inner-icon="mdi-source-branch"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-autocomplete
              v-model="cicloDestino"
              :items="ciclosDestino"
              label="Ciclo destino"
              filled
              dense
              hide-details
              return-object
              item-text="ciclo"
              item-value="id_ciclo"
              prepend-inner-icon="mdi-target"
            />
          </v-col>
          <v-col cols="12">
            <v-autocomplete
              v-model="plantelesSel"
              :items="plantelesErp"
              :loading="cargandoPlanteles"
              label="Planteles activos"
              filled
              dense
              hide-details
              multiple
              chips
              small-chips
              deletable-chips
              item-text="plantel"
              item-value="id_plantel"
              prepend-inner-icon="mdi-office-building"
            >
              <template v-slot:prepend-item>
                <v-list-item ripple @click="toggleTodosPlanteles">
                  <v-list-item-action>
                    <v-icon :color="plantelesSel.length ? 'primary' : ''">
                      {{ iconoTodosPlanteles }}
                    </v-icon>
                  </v-list-item-action>
                  <v-list-item-content>
                    <v-list-item-title>Seleccionar todos</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-divider class="mt-2"></v-divider>
              </template>
            </v-autocomplete>
          </v-col>
        </v-row>

        <v-row dense class="mt-1">
          <v-col cols="12" sm="6">
            <v-checkbox
              v-model="limpiarDestino"
              hide-details
              dense
              label="Limpiar maestrosgrupo y LMS del ciclo destino antes de copiar"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-checkbox
              v-model="sincronizarLms"
              hide-details
              dense
              label="Sincronizar grupo_teachers (ing_brain / ing_brain_fast) al finalizar"
            />
          </v-col>
        </v-row>

        <v-row v-if="preview" dense class="mt-3 mb-2">
          <v-col cols="6" sm="3">
            <v-card outlined class="pa-2 text-center">
              <div class="text-h6 font-weight-bold">{{ preview.resumen.grupos_mapeados }}</div>
              <div class="text-caption grey--text">Grupos mapeados</div>
            </v-card>
          </v-col>
          <v-col cols="6" sm="3">
            <v-card outlined class="pa-2 text-center" style="border-color:#43A047">
              <div class="text-h6 font-weight-bold green--text">{{ preview.resumen.grupos_con_maestros }}</div>
              <div class="text-caption grey--text">Con maestros</div>
            </v-card>
          </v-col>
          <v-col cols="6" sm="3">
            <v-card outlined class="pa-2 text-center">
              <div class="text-h6 font-weight-bold">{{ preview.resumen.maestros_a_copiar }}</div>
              <div class="text-caption grey--text">Registros a copiar</div>
            </v-card>
          </v-col>
          <v-col cols="6" sm="3">
            <v-card outlined class="pa-2 text-center" style="border-color:#E53935">
              <div class="text-h6 font-weight-bold red--text">{{ preview.resumen.grupos_sin_maestros }}</div>
              <div class="text-caption grey--text">Sin maestro origen</div>
            </v-card>
          </v-col>
        </v-row>

        <v-data-table
          v-if="preview && preview.detalle.length"
          :headers="headers"
          :items="preview.detalle"
          dense
          class="elevation-0"
          :items-per-page="8"
          item-key="id_grupo_origen"
        >
          <template v-slot:item.maestros="{ item }">
            <div v-for="m in item.maestros" :key="`${item.id_grupo_origen}-${m.numero_teacher}`" class="text-caption">
              T{{ m.numero_teacher }}: {{ m.teacher || m.id_maestro }}
            </div>
          </template>
          <template v-slot:item.flecha>
            <v-icon small color="primary">mdi-arrow-right</v-icon>
          </template>
        </v-data-table>

        <v-alert
          v-else-if="preview && preview.resumen.grupos_mapeados === 0"
          type="warning"
          dense
          outlined
          class="mt-3"
        >
          No hay grupos con <code>id_grupo_consecutivo</code> válido entre los ciclos y planteles seleccionados.
          Verifica que el ciclo origen tenga grupos ligados al destino.
        </v-alert>

        <v-alert
          v-else-if="preview && preview.resumen.grupos_mapeados > 0 && !preview.detalle.length"
          type="warning"
          dense
          outlined
          class="mt-3"
        >
          Hay {{ preview.resumen.grupos_mapeados }} grupos mapeados, pero ninguno tiene maestro en
          <code>maestrosgrupo</code> del ciclo origen.
        </v-alert>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions class="pa-3">
        <v-btn text @click="cerrar">Cancelar</v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          outlined
          :loading="cargandoPreview"
          :disabled="!puedeConsultar"
          @click="consultarPreview"
        >
          <v-icon left small>mdi-magnify</v-icon>
          Vista previa
        </v-btn>
        <v-btn
          color="primary"
          dark
          :loading="ejecutando"
          :disabled="!preview || !preview.detalle.length"
          @click="confirmarEjecucion"
        >
          <v-icon left small>mdi-content-copy</v-icon>
          Copiar maestros
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import validarErrores from '@/mixins/validarErrores'

export default {
  mixins: [validarErrores],

  props: {
    dialog: { type: Object, required: true },
    ciclos: { type: Array, default: () => [] },
  },

  data: () => ({
    cicloOrigen: null,
    cicloDestino: null,
    plantelesErp: [],
    plantelesSel: [],
    limpiarDestino: true,
    sincronizarLms: true,
    preview: null,
    cargandoPlanteles: false,
    cargandoPreview: false,
    ejecutando: false,
    headers: [
      { text: 'Plantel', value: 'plantel' },
      { text: 'Grupo origen', value: 'grupo_origen' },
      { text: '', value: 'flecha', sortable: false, width: 40 },
      { text: 'Grupo destino', value: 'grupo_destino' },
      { text: 'Maestros', value: 'maestros', sortable: false },
    ],
  }),

  computed: {
    ciclosDestino() {
      if (!this.cicloOrigen) return this.ciclos
      return this.ciclos.filter((c) => c.id_ciclo !== this.cicloOrigen.id_ciclo)
    },
    puedeConsultar() {
      return this.cicloOrigen && this.cicloDestino && this.plantelesSel.length > 0
    },
    iconoTodosPlanteles() {
      if (!this.plantelesErp.length) return 'mdi-checkbox-blank-outline'
      if (this.plantelesSel.length === this.plantelesErp.length) return 'mdi-checkbox-marked'
      if (this.plantelesSel.length) return 'mdi-minus-box'
      return 'mdi-checkbox-blank-outline'
    },
  },

  watch: {
    'dialog.estatus'(val) {
      if (val) {
        this.preview = null
        this.cargarPlantelesErp()
      }
    },
    cicloOrigen() {
      this.preview = null
      if (this.cicloDestino && this.cicloOrigen && this.cicloDestino.id_ciclo === this.cicloOrigen.id_ciclo) {
        this.cicloDestino = null
      }
    },
    cicloDestino() { this.preview = null },
    plantelesSel() { this.preview = null },
  },

  mounted() {
    if (this.dialog.estatus) {
      this.cargarPlantelesErp()
    }
  },

  methods: {
    cerrar() {
      this.dialog.estatus = false
    },

    esPlantelActivo(plantel) {
      const id = plantel.id_plantel || plantel.idplantel
      if (!plantel || !id) return false
      if (plantel.activo_sn != null && Number(plantel.activo_sn) !== 1) return false
      const baja = plantel.fecha_baja ? String(plantel.fecha_baja).slice(0, 10) : null
      if (baja && baja !== '0000-00-00' && baja !== 'null') return false
      return true
    },

    normalizarPlantel(plantel) {
      return {
        ...plantel,
        id_plantel: plantel.id_plantel || plantel.idplantel,
      }
    },

    cargarPlantelesErp() {
      this.cargandoPlanteles = true
      this.$http.get('planteles.activos')
        .then((response) => {
          const lista = Array.isArray(response.data) ? response.data : []
          this.plantelesErp = lista
            .map((p) => this.normalizarPlantel(p))
            .filter((p) => this.esPlantelActivo(p))
            .sort((a, b) => (a.plantel || '').localeCompare(b.plantel || '', 'es'))
          // Quitar selección de planteles que ya no están activos
          const idsActivos = new Set(this.plantelesErp.map((p) => p.id_plantel))
          this.plantelesSel = this.plantelesSel.filter((id) => idsActivos.has(id))
        })
        .catch((error) => {
          this.validarError(error.response && error.response.data ? error.response.data.message : 'Error al cargar planteles')
        })
        .finally(() => { this.cargandoPlanteles = false })
    },

    toggleTodosPlanteles() {
      if (this.plantelesSel.length === this.plantelesErp.length) {
        this.plantelesSel = []
      } else {
        this.plantelesSel = this.plantelesErp.map((p) => p.id_plantel)
      }
    },

    payloadBase() {
      return {
        id_ciclo_origen: this.cicloOrigen.id_ciclo,
        id_ciclo_destino: this.cicloDestino.id_ciclo,
        id_ciclo_relacionado_destino: this.cicloDestino.id_ciclo_relacionado,
        id_planteles: this.plantelesSel.map((id) => parseInt(id, 10)).filter((id) => id > 0),
      }
    },

    consultarPreview() {
      if (!this.puedeConsultar) {
        return this.validarErrorDirecto('Completa ciclo origen, destino y planteles')
      }
      this.cargandoPreview = true
      this.$http.post('rol.copiar.ciclo.preview', this.payloadBase())
        .then((response) => {
          this.preview = response.data
        })
        .catch((error) => {
          this.validarError(error.response && error.response.data ? error.response.data.message : 'Error en vista previa')
        })
        .finally(() => { this.cargandoPreview = false })
    },

    confirmarEjecucion() {
      if (!this.preview || !this.preview.detalle.length) return

      const msg = `Se copiarán ${this.preview.resumen.maestros_a_copiar} registros de maestros ` +
        `en ${this.preview.resumen.grupos_con_maestros} grupos. ¿Continuar?`

      if (!window.confirm(msg)) return
      this.ejecutar()
    },

    ejecutar() {
      this.ejecutando = true
      this.$http.post('rol.copiar.ciclo.ejecutar', {
        ...this.payloadBase(),
        limpiar_destino: this.limpiarDestino,
        sincronizar_lms: this.sincronizarLms,
      })
        .then((response) => {
          const r = response.data.resumen || {}
          this.validarSuccess(
            `${response.data.message}. Insertados: ${r.registros_insertados || 0}, actualizados: ${r.registros_actualizados || 0}`
          )
          this.$emit('completado')
          this.cerrar()
        })
        .catch((error) => {
          this.validarError(error.response && error.response.data ? error.response.data.message : 'Error al copiar')
        })
        .finally(() => { this.ejecutando = false })
    },
  },
}
</script>
